import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import Label from '../forms/label.jsx';
import Input from '../forms/input.jsx';
import BufferedInputHOC from '../forms/buffered-input-hoc.jsx';
import ToggleButtons from '../toggle-buttons/toggle-buttons.jsx';
import Dial from './dial.jsx';

import styles from './direction-picker.css';

import allAroundIcon from './icon--all-around.svg';
import leftRightIcon from './icon--left-right.svg';
import dontRotateIcon from './icon--dont-rotate.svg';

const BufferedInput = BufferedInputHOC(Input);

const directionLabel = (
    <FormattedMessage
        defaultMessage="Direction"
        description="Sprite info direction label"
        id="gui.SpriteInfo.direction"
    />
);

const RotationStyles = {
    ALL_AROUND: 'all around',
    LEFT_RIGHT: 'left-right',
    DONT_ROTATE: "don't rotate"
};

const messages = defineMessages({
    allAround: {
        id: 'gui.directionPicker.rotationStyles.allAround',
        description: 'Button to change to the all around rotation style',
        defaultMessage: 'All Around'
    },
    leftRight: {
        id: 'gui.directionPicker.rotationStyles.leftRight',
        description: 'Button to change to the left-right rotation style',
        defaultMessage: 'Left/Right'
    },
    dontRotate: {
        id: 'gui.directionPicker.rotationStyles.dontRotate',
        description: 'Button to change to the dont rotate rotation style',
        defaultMessage: 'Do not rotate'
    }
});

const DirectionPicker = ({
    direction,
    disabled,
    labelAbove = false,
    onChangeDirection,
    onClickAllAround,
    onClickDontRotate,
    onClickLeftRight,
    onClosePopover,
    onOpenPopover,
    popoverOpen,
    rotationStyle
}) => {
    const intl = useIntl();
    const popoverRef = useRef(null);

    useEffect(() => {
        if (!popoverOpen) return;

        const handleOuterAction = event => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClosePopover();
            }
        };

        document.addEventListener('mousedown', handleOuterAction, true);
        document.addEventListener('touchstart', handleOuterAction, true);

        return () => {
            document.removeEventListener('mousedown', handleOuterAction, true);
            document.removeEventListener('touchstart', handleOuterAction, true);
        };
    }, [onClosePopover, popoverOpen]);

    return (
        <Label
            secondary
            above={labelAbove}
            text={directionLabel}
        >
            <div
                className={styles.popoverWrapper}
                ref={popoverRef}
            >
                <BufferedInput
                    small
                    disabled={disabled}
                    label={directionLabel}
                    tabIndex="0"
                    type="text"
                    value={disabled ? '' : direction}
                    onFocus={onOpenPopover}
                    onSubmit={onChangeDirection}
                />
                {popoverOpen && (
                    <div
                        className={styles.popoverBody}
                        role="dialog"
                    >
                        <Dial
                            direction={direction}
                            onChange={onChangeDirection}
                        />
                        <ToggleButtons
                            className={styles.buttonRow}
                            buttons={[
                                {
                                    handleClick: onClickAllAround,
                                    icon: allAroundIcon,
                                    isSelected: rotationStyle === RotationStyles.ALL_AROUND,
                                    title: intl.formatMessage(messages.allAround)
                                },
                                {
                                    handleClick: onClickLeftRight,
                                    icon: leftRightIcon,
                                    isSelected: rotationStyle === RotationStyles.LEFT_RIGHT,
                                    title: intl.formatMessage(messages.leftRight)
                                },
                                {
                                    handleClick: onClickDontRotate,
                                    icon: dontRotateIcon,
                                    isSelected: rotationStyle === RotationStyles.DONT_ROTATE,
                                    title: intl.formatMessage(messages.dontRotate)
                                }
                            ]}
                        />
                    </div>
                )}
            </div>
        </Label>
    );
};

DirectionPicker.propTypes = {
    direction: PropTypes.number,
    disabled: PropTypes.bool.isRequired,
    labelAbove: PropTypes.bool,
    onChangeDirection: PropTypes.func.isRequired,
    onClickAllAround: PropTypes.func.isRequired,
    onClickDontRotate: PropTypes.func.isRequired,
    onClickLeftRight: PropTypes.func.isRequired,
    onClosePopover: PropTypes.func.isRequired,
    onOpenPopover: PropTypes.func.isRequired,
    popoverOpen: PropTypes.bool.isRequired,
    rotationStyle: PropTypes.string
};

const WrappedDirectionPicker = DirectionPicker;

export {
    WrappedDirectionPicker as default,
    RotationStyles
};
